import { Download, FileSpreadsheet, Layers3, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  livrablesTangiblesParTactique,
  type LivrableTangibleParTactique,
} from '../data/livrablesTangiblesParTactique';
import { projetEHCyberHolistiqueTasks } from '../data/projetEHCyberHolistiqueTasks';

interface ProjetEHTangiblesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type PilierClient = {
  id: number;
  code: string;
  nom: string;
  badgeClass: string;
};

type TransversaleClient = {
  id: 'actifs' | 'projet';
  nom: string;
  badgeClass: string;
};

type AssociationLivrable = {
  pilier: PilierClient;
  transversales: TransversaleClient[];
};

type TableauTangibleItem = LivrableTangibleParTactique & {
  sourceLabel?: string;
  sourceUrl?: string;
};

const piliersClient: PilierClient[] = [
  {
    id: 1,
    code: 'P1',
    nom: 'Gouvernance, stratégie & organisation',
    badgeClass: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
  },
  {
    id: 2,
    code: 'P2',
    nom: 'Architecture, infrastructure & cloud',
    badgeClass: 'bg-sky-100 text-sky-800 border-sky-300',
  },
  {
    id: 3,
    code: 'P3',
    nom: 'Applications, données & intégrations',
    badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  },
  {
    id: 4,
    code: 'P4',
    nom: 'Services, opérations & expérience utilisateur',
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-300',
  },
  {
    id: 5,
    code: 'P5',
    nom: 'Cybersécurité, risque & conformité',
    badgeClass: 'bg-rose-100 text-rose-800 border-rose-300',
  },
  {
    id: 6,
    code: 'P6',
    nom: 'Transformation, innovation & IA',
    badgeClass: 'bg-violet-100 text-violet-800 border-violet-300',
  },
];

const transversalesClient: Record<TransversaleClient['id'], TransversaleClient> = {
  actifs: {
    id: 'actifs',
    nom: 'Gestion des actifs & parties prenantes',
    badgeClass: 'bg-teal-100 text-teal-800 border-teal-300',
  },
  projet: {
    id: 'projet',
    nom: 'Gestion de projet & amélioration continue',
    badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  },
};

const profondeurColors: Record<LivrableTangibleParTactique['profondeur'], string> = {
  Humaine: 'bg-blue-100 text-blue-800 border-blue-300',
  Procédurale: 'bg-purple-100 text-purple-800 border-purple-300',
  Technologique: 'bg-green-100 text-green-800 border-green-300',
  Mixte: 'bg-orange-100 text-orange-800 border-orange-300',
};

const typeColors: Record<LivrableTangibleParTactique['type'], string> = {
  Document: 'bg-gray-100 text-gray-800',
  Outil: 'bg-indigo-100 text-indigo-800',
  Dashboard: 'bg-cyan-100 text-cyan-800',
  Rapport: 'bg-blue-100 text-blue-800',
  Matrice: 'bg-pink-100 text-pink-800',
  Présentation: 'bg-yellow-100 text-yellow-800',
  Processus: 'bg-emerald-100 text-emerald-800',
};

const standardColors: Record<LivrableTangibleParTactique['standard'], string> = {
  ITIL: 'bg-blue-500 text-white',
  COBIT: 'bg-purple-500 text-white',
  NIST: 'bg-green-500 text-white',
  ISO: 'bg-red-500 text-white',
  PMBOK: 'bg-orange-500 text-white',
  RACI: 'bg-indigo-500 text-white',
  Agile: 'bg-yellow-500 text-white',
  Scrum: 'bg-cyan-500 text-white',
  Kanban: 'bg-teal-500 text-white',
  DevOps: 'bg-pink-500 text-white',
  BZ: 'bg-violet-500 text-white',
  Formation: 'bg-amber-500 text-white',
  Autre: 'bg-gray-500 text-white',
};

const oldPilierAbrev: Record<number, string> = {
  1: 'STG',
  2: 'ACT',
  3: 'POR',
  4: 'SER',
  5: 'CYB',
  6: 'AME',
  7: 'INN',
};

function includesAny(text: string, keywords: string[]) {
  return keywords.some((keyword) => text.includes(keyword));
}

function getText(livrable: TableauTangibleItem) {
  return `${livrable.pilierNom} ${livrable.tactiqueNom} ${livrable.livrableNom} ${livrable.description} ${livrable.type} ${livrable.standard}`.toLowerCase();
}

function getPilierClient(livrable: TableauTangibleItem): PilierClient {
  const text = getText(livrable);

  if (
    includesAny(text, [
      'cyber',
      'sécurité',
      'securite',
      'risque',
      'conformité',
      'conformite',
      'nist',
      'iso 27001',
      'vulnérabilité',
      'vulnerabilite',
      'mfa',
      'edr',
      'siem',
      'phishing',
      'contrôle',
      'controle',
      'protection',
      'réponse',
      'reprise',
    ])
  ) {
    return piliersClient[4];
  }

  if (
    includesAny(text, [
      'infrastructure',
      'architecture',
      'serveur',
      'réseau',
      'reseau',
      'cloud',
      'infonuagique',
      'backup',
      'sauvegarde',
      'redondance',
      'hébergement',
      'hebergement',
      'capacité',
      'capacite',
      'poste',
      'tenant',
      'intune',
      'plateforme',
    ])
  ) {
    return piliersClient[1];
  }

  if (
    includesAny(text, [
      'application',
      'applicatif',
      'donnée',
      'donnee',
      'données',
      'donnees',
      'intégration',
      'integration',
      'api',
      'flux',
      'cmdb',
      'licence',
      'inventaire',
      'catalogue',
      'actif',
      'système métier',
      'systeme metier',
    ])
  ) {
    return piliersClient[2];
  }

  if (
    includesAny(text, [
      'service',
      'support',
      'incident',
      'demande',
      'changement',
      'ticket',
      'sla',
      'opération',
      'operation',
      'disponibilité',
      'disponibilite',
      'expérience utilisateur',
      'experience utilisateur',
      'service desk',
    ])
  ) {
    return piliersClient[3];
  }

  if (
    includesAny(text, [
      'ia',
      'innovation',
      'transformation',
      'automatisation',
      'amélioration',
      'amelioration',
      'optimisation',
      'modernisation',
      'veille',
      'devops',
      'agile',
      'scrum',
      'kanban',
      'programmation',
    ])
  ) {
    return piliersClient[5];
  }

  if ([1, 3].includes(livrable.pilierId)) {
    return piliersClient[0];
  }
  if (livrable.pilierId === 2) {
    return piliersClient[2];
  }
  if (livrable.pilierId === 4) {
    return piliersClient[3];
  }
  if (livrable.pilierId === 5) {
    return piliersClient[4];
  }
  if ([6, 7].includes(livrable.pilierId)) {
    return piliersClient[5];
  }

  return piliersClient[0];
}

function getTransversales(livrable: TableauTangibleItem): TransversaleClient[] {
  const text = getText(livrable);
  const transversales: TransversaleClient[] = [];

  if (
    includesAny(text, [
      'actif',
      'inventaire',
      'utilisateur',
      'partie prenante',
      'owner',
      'ownership',
      'propriétaire',
      'proprietaire',
      'responsabilité',
      'responsabilite',
      'rôle',
      'role',
      'raci',
      'cmdb',
      'catalogue',
      'licence',
      'dépendance',
      'dependance',
      'client',
      'équipe',
      'equipe',
    ])
  ) {
    transversales.push(transversalesClient.actifs);
  }

  if (
    includesAny(text, [
      'projet',
      'portefeuille',
      'roadmap',
      'plan d’action',
      "plan d'action",
      'jalon',
      'budget',
      'amélioration',
      'amelioration',
      'post-mortem',
      'qualité',
      'qualite',
      'leçon',
      'lecon',
      'maturité',
      'maturite',
      'progression',
      'optimisation',
      'indicateur',
      'kpi',
      'dashboard',
      'backlog',
      'priorisation',
      'performance',
      'mesure',
    ])
  ) {
    transversales.push(transversalesClient.projet);
  }

  return transversales;
}

function getAssociation(livrable: TableauTangibleItem): AssociationLivrable {
  return {
    pilier: getPilierClient(livrable),
    transversales: getTransversales(livrable),
  };
}

function exportTangiblesToExcel(
  associations: { livrable: TableauTangibleItem; association: AssociationLivrable }[]
) {
  const rows = associations.map(({ livrable, association }) => ({
    'Pilier cadre client': `${association.pilier.code} - ${association.pilier.nom}`,
    Transversales:
      association.transversales.length > 0
        ? association.transversales.map((transversale) => transversale.nom).join(' | ')
        : 'Aucune',
    'Pilier source': livrable.sourceLabel ? `CH - ${livrable.sourceLabel}` : `P${livrable.pilierId} - ${oldPilierAbrev[livrable.pilierId] || 'N/A'} - ${livrable.pilierNom}`,
    'ID tactique': livrable.tactiqueId,
    Tactique: livrable.tactiqueNom,
    'Livrable tangible': livrable.livrableNom,
    Description: livrable.description,
    Type: livrable.type,
    Standard: livrable.standard,
    Profondeur: livrable.profondeur,
    Source: livrable.sourceLabel || 'Cadre référentiel BZ',
    'Lien source': livrable.sourceUrl || '',
  }));

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(rows);
  worksheet['!cols'] = [
    { wch: 34 },
    { wch: 44 },
    { wch: 34 },
    { wch: 14 },
    { wch: 34 },
    { wch: 42 },
    { wch: 80 },
    { wch: 16 },
    { wch: 16 },
    { wch: 18 },
    { wch: 24 },
    { wch: 48 },
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Tableau tangibles');
  XLSX.writeFile(workbook, `Projet_EH_Tableau_des_tangibles_${new Date().toISOString().split('T')[0]}.xlsx`);
}

export default function ProjetEHTangiblesModal({ isOpen, onClose }: ProjetEHTangiblesModalProps) {
  if (!isOpen) return null;

  const tableauTangibles: TableauTangibleItem[] = [
    ...livrablesTangiblesParTactique,
    ...projetEHCyberHolistiqueTasks,
  ];

  const associations = tableauTangibles
    .map((livrable, sourceIndex) => ({
      livrable,
      association: getAssociation(livrable),
      sourceIndex,
    }))
    .sort((a, b) => {
      if (a.association.pilier.id !== b.association.pilier.id) {
        return a.association.pilier.id - b.association.pilier.id;
      }

      return a.sourceIndex - b.sourceIndex;
    });

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-50 rounded-2xl shadow-2xl max-w-[96vw] w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-6 py-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="bg-emerald-500 p-3 rounded-xl">
              <FileSpreadsheet className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black">Tableau des tangibles</h2>
              <p className="text-sm text-slate-300 mt-1">
                Livrables et tactiques associés à un pilier principal du cadre client BZ, avec badges transversaux.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Fermer le tableau des tangibles"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 overflow-auto">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
              <div className="bg-emerald-100 text-emerald-800 p-2 rounded-lg">
                <Layers3 className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-black text-gray-900">Correspondance livrables, piliers et transversales</h3>
                <p className="text-sm text-gray-600">
                  {tableauTangibles.length} livrables : cadre référentiel BZ et tâches du modèle Cyber Holistique.
                </p>
              </div>
              <button
                type="button"
                onClick={() => exportTangiblesToExcel(associations)}
                className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-emerald-700"
                title="Exporter le tableau des tangibles en Excel"
              >
                <Download className="h-4 w-4" />
                Exporter Excel
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-gray-800 to-gray-900 text-white sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-bold border-r border-gray-700 min-w-[230px]">Pilier cadre client</th>
                    <th className="px-2 py-2 text-left text-xs font-bold border-r border-gray-700 min-w-[230px]">Transversales</th>
                    <th className="px-2 py-2 text-left text-xs font-bold border-r border-gray-700 min-w-[80px]">Pilier source</th>
                    <th className="px-2 py-2 text-left text-xs font-bold border-r border-gray-700 min-w-[170px]">Tactique</th>
                    <th className="px-2 py-2 text-left text-xs font-bold border-r border-gray-700 min-w-[190px]">Livrable tangible</th>
                    <th className="px-2 py-2 text-left text-xs font-bold border-r border-gray-700 min-w-[260px]">Description</th>
                    <th className="px-2 py-2 text-center text-xs font-bold border-r border-gray-700 min-w-[90px]">Type</th>
                    <th className="px-2 py-2 text-center text-xs font-bold border-r border-gray-700 min-w-[90px]">Standard</th>
                    <th className="px-2 py-2 text-center text-xs font-bold min-w-[110px]">Profondeur</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {associations.map(({ livrable, association }, index) => {
                    const isCyberHolistique = Boolean(livrable.sourceLabel);

                    return (
                    <tr
                      key={livrable.id}
                      className={`transition-colors ${
                        isCyberHolistique
                          ? 'bg-emerald-50/80 hover:bg-emerald-100/80 border-l-4 border-emerald-500'
                          : `hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`
                      }`}
                    >
                      <td className="px-2 py-2 border-r border-gray-200 align-top">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-bold ${association.pilier.badgeClass}`}>
                          {association.pilier.code}
                          <span className="font-semibold">{association.pilier.nom}</span>
                        </span>
                      </td>
                      <td className="px-2 py-2 border-r border-gray-200 align-top">
                        {association.transversales.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {association.transversales.map((transversale) => (
                              <span
                                key={`${livrable.id}-${transversale.id}`}
                                className={`inline-flex rounded-full border px-2 py-1 text-xs font-bold ${transversale.badgeClass}`}
                              >
                                {transversale.nom}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs font-semibold text-gray-400">Aucune</span>
                        )}
                      </td>
                      <td className="px-2 py-2 border-r border-gray-200 align-top">
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-gray-700">
                            {livrable.sourceLabel ? 'CH' : `P${livrable.pilierId}`}
                          </span>
                          {!livrable.sourceLabel && (
                            <span className="text-xs font-semibold text-gray-800">
                              {oldPilierAbrev[livrable.pilierId] || 'N/A'}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                          {livrable.sourceLabel || livrable.pilierNom}
                        </p>
                      </td>
                      <td className="px-2 py-2 border-r border-gray-200 align-top">
                        <p className="font-bold text-gray-900 text-xs">{livrable.tactiqueId}</p>
                        <p className="text-xs text-gray-700 leading-snug mt-1">{livrable.tactiqueNom}</p>
                      </td>
                      <td className="px-2 py-2 border-r border-gray-200 align-top">
                        <p className="font-semibold text-gray-900 leading-snug">{livrable.livrableNom}</p>
                        {livrable.sourceUrl && (
                          <a
                            href={livrable.sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex text-[11px] font-semibold text-emerald-700 hover:text-emerald-900"
                          >
                            Ouvrir dans Asana
                          </a>
                        )}
                      </td>
                      <td className="px-2 py-2 border-r border-gray-200 align-top">
                        <p className="text-xs text-gray-700 leading-relaxed">{livrable.description}</p>
                      </td>
                      <td className="px-2 py-2 text-center border-r border-gray-200 align-top">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${typeColors[livrable.type]}`}>
                          {livrable.type}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center border-r border-gray-200 align-top">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${standardColors[livrable.standard]}`}>
                          {livrable.standard}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center align-top">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold border ${profondeurColors[livrable.profondeur]}`}>
                          {livrable.profondeur}
                        </span>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
